import { useState, useEffect, useCallback, useRef } from 'react'
import type { FoundryAsset, AssetType } from '../types'

interface Props {
  type: AssetType
  selectedIds: string[]
  maxSelections?: number
  onSelectionChange: (ids: string[]) => void
  suggestedTags?: string[]
}

export function FoundryPicker({ type, selectedIds, maxSelections = 3, onSelectionChange, suggestedTags }: Props) {
  const [assets, setAssets] = useState<FoundryAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const fetchAssets = useCallback(async (search?: string) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ type, limit: '30' })
      if (search?.trim()) params.set('search', search.trim())
      if (suggestedTags?.length && !search?.trim()) params.set('tags', suggestedTags.join(','))

      const res = await fetch(`/api/foundry/assets?${params}`)
      const data = await res.json()
      if (Array.isArray(data)) setAssets(data)
    } catch {
      setAssets([])
    } finally {
      setLoading(false)
    }
  }, [type, suggestedTags])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchQuery(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchAssets(val), 300)
  }

  const toggleAsset = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id))
    } else if (selectedIds.length < maxSelections) {
      onSelectionChange([...selectedIds, id])
    }
  }

  const selectedAssets = assets.filter((a) => selectedIds.includes(a.id))
  const unselectedAssets = assets.filter((a) => !selectedIds.includes(a.id))

  return (
    <div>
      {/* Selected assets preview */}
      {selectedIds.length > 0 && (
        <div className="flex gap-2 mb-2">
          {selectedAssets.map((a) => (
            <div key={a.id} className="relative group">
              <img
                src={a.url}
                alt={a.name}
                className="w-14 h-14 rounded-md object-cover border border-forge-ember"
              />
              <button
                onClick={() => toggleAsset(a.id)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-forge-heat-critical text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                ×
              </button>
              <p className="text-[8px] text-forge-ash text-center truncate w-14 mt-0.5">{a.name}</p>
            </div>
          ))}
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-[10px] text-forge-ember hover:text-forge-ember/80 font-semibold"
      >
        {expanded ? '− Close Foundry' : `+ Browse ${type === 'character' ? 'Characters' : 'Scenes'}`}
        {selectedIds.length > 0 && ` (${selectedIds.length}/${maxSelections})`}
      </button>

      {/* Expanded picker */}
      {expanded && (
        <div className="mt-2 border border-forge-slate rounded-lg bg-forge-obsidian/50 overflow-hidden">
          {/* Search */}
          <div className="px-3 py-2 border-b border-forge-slate/50">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={`Search ${type}s...`}
              className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-2.5 py-1 text-[11px] text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none"
            />
          </div>

          {/* Grid */}
          <div className="max-h-48 overflow-auto p-2">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-4 h-4 border-2 border-forge-ember border-t-transparent rounded-full animate-spin" />
              </div>
            ) : assets.length === 0 ? (
              <p className="text-[10px] text-forge-cool-smoke text-center py-4">
                No {type}s in the Foundry yet. Create some in the Foundry tool first.
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-1.5">
                {[...selectedAssets, ...unselectedAssets].map((a) => {
                  const selected = selectedIds.includes(a.id)
                  const disabled = !selected && selectedIds.length >= maxSelections
                  return (
                    <button
                      key={a.id}
                      onClick={() => !disabled && toggleAsset(a.id)}
                      disabled={disabled}
                      className={`relative rounded-md overflow-hidden border transition-all ${
                        selected
                          ? 'border-forge-ember ring-1 ring-forge-ember/50'
                          : disabled
                            ? 'border-forge-slate opacity-40 cursor-not-allowed'
                            : 'border-forge-slate hover:border-forge-ember/40'
                      }`}
                    >
                      <div className="aspect-square">
                        <img src={a.url} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <p className="text-[8px] text-forge-ash truncate px-1 py-0.5 bg-forge-obsidian">{a.name}</p>
                      {selected && (
                        <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-forge-ember flex items-center justify-center">
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
